import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../../lib/api.js'
import { normalizeChampionships, attachCitationFanout as attachFanoutOnly } from '../../lib/championships.js'
import { attachSplitFamilies } from './pursuit/splitFamily.js'
import { championshipSourceEvidence } from './championshipPlaneEvidence.js'
import { MOCK_CHAMPIONSHIPS } from './mockChampionships.js'

/* ⭐ SPLIT FAMILIES ARE STAMPED WHERE THE FAN-OUT IS, AND FOR THE SAME REASON:
 * both are universe-level facts a row cannot compute about itself, and both
 * are only true over a COMPLETE universe. Every call site that stamps one
 * stamps the other, so the board and the worklist read the same families. */
const attachCitationFanout = (records) => attachSplitFamilies(attachFanoutOnly(records))

/**
 * Load the FULL championship universe from GET /api/championships.
 *
 * The API caps at 100 records/page and returns an `after` cursor in `paging`.
 * The previous build only fetched page 1 — the root cause of empty maps,
 * one-bar rep load, and monoculture filters.
 *
 * FULL DYNAMIC LOAD: this follows the cursor to EXHAUSTION. There is no page
 * cap and no hardcoded expected total anywhere — the loop ends when and only
 * when HubSpot stops handing back an `after` cursor. Whatever the object holds
 * on the day it is loaded is what renders, and `loadedCount` (the live count
 * surfaced in the UI) is counted off the query itself, never from a constant.
 *
 * Read-only. This hook never writes to HubSpot. `?demo=1` loads bundled
 * fictional data.
 */
function isDemo() {
  if (typeof window === 'undefined') return false
  return new URLSearchParams(window.location.search).get('demo') === '1'
}

const PAGE = 100
// Runaway backstop ONLY — a cursor that never terminates would otherwise spin
// forever. Set far above any plausible object size so it never truncates a real
// load; if it were ever hit the UI says so instead of silently under-reporting.
const RUNAWAY_PAGES = 100000

/* How long to wait on a snapshot that is still building before giving up and
 * taking the slow walk. A cold build is ~47s; 90s leaves headroom without
 * leaving somebody staring at a spinner indefinitely. */
const SNAPSHOT_WAIT_MS = 90_000
const SNAPSHOT_POLL_MS = 2_000

export function useChampionships() {
  const demo = isDemo()
  const [data, setData] = useState([])
  const [status, setStatus] = useState('loading') // loading | done | error
  const [error, setError] = useState('')
  const [loadedCount, setLoadedCount] = useState(0)
  const [pageCount, setPageCount] = useState(0)
  const [complete, setComplete] = useState(false) // whole object loaded?
  /* HOW the universe arrived: 'snapshot' (one prebuilt request) or 'paged' (the
   * cursor walk). The receipt used to say "over 1 pages — cursor exhausted" for
   * the snapshot path, which walks no cursor and makes no pages. Both halves of
   * that sentence were wrong, and it is the sentence whose whole job is to
   * prove the full-universe load. */
  const [loadMode, setLoadMode] = useState('')
  /* How old the served set is. null when the paged fallback ran — that walk IS
   * live, so claiming an age for it would be the opposite lie. */
  const [asOf, setAsOf] = useState(null)
  const [readEvidence, setReadEvidence] = useState(null)
  const [readScope, setReadScope] = useState(null)
  /* ⚠️ WHY THIS EXISTS: the slow path used to be reached SILENTLY. The board
   * looked merely slow, so the fast path stayed broken in production for days
   * while everything downstream reported green. If we degrade, we say so. */
  const [degraded, setDegraded] = useState('')
  /* Progress of a server-side snapshot build, so the wait is explained rather
   * than being an unattributed spinner. null when not building. */
  const [building, setBuilding] = useState(null)
  const runId = useRef(0)

  const load = useCallback(
    async (signal) => {
      const rid = ++runId.current
      setStatus('loading')
      setError('')
      setDegraded('')
      setBuilding(null)
      setData([])
      setLoadedCount(0)
      setPageCount(0)
      setComplete(false)
      setLoadMode('')
      setAsOf(null)
      setReadEvidence(null)
      setReadScope(null)

      if (demo) {
        setData(attachCitationFanout(normalizeChampionships(MOCK_CHAMPIONSHIPS)))
        setLoadedCount(MOCK_CHAMPIONSHIPS.length)
        setComplete(true)
        setStatus('done')
        return
      }

      /* ⚠️ ONE REQUEST INSTEAD OF ~148.
       *
       * Brian: "It might take 2-4 minutes to fully load every single
       * championship." The cause was round-trips, not volume: this hook paged
       * 100 at a time to exhaustion, and every page was a full
       * browser → Railway → HubSpot hop. The backend now builds the set once
       * and serves it whole from `GET /api/championships/all`.
       *
       * ⚠️ THE PAGED WALK BELOW IS KEPT AS THE FALLBACK, not deleted. If the
       * snapshot route is unreachable — an old backend, a deploy in flight —
       * the board must still load rather than show an empty universe. A
       * performance change that can take the Command Center down is not worth
       * making. */
      /* ⚠️ A 202 MEANS "BUILDING" — POLL IT, DO NOT FALL BACK.
       *
       * This is the bug that made the board take minutes even after the
       * snapshot shipped. A cold snapshot took ~47s server-side and Railway's
       * gateway cuts at 30, so the request failed, the `catch` below swallowed
       * it, and the board quietly walked HubSpot 148 times — with nothing on
       * screen saying the fast path had been abandoned. The backend now answers
       * 202 with progress instead of hanging, and this loop waits for it.
       *
       * Bounded: after SNAPSHOT_WAIT_MS we stop waiting and take the slow walk,
       * because a board that never loads is worse than a slow one. */
      let degradedReason = ''
      try {
        const deadline = Date.now() + SNAPSHOT_WAIT_MS
        for (;;) {
          const snap = await api.championshipsAll({ signal })
          if (signal?.aborted || rid !== runId.current) return

          if (snap?.building) {
            if (Date.now() > deadline) {
              degradedReason = 'The server was still building the championship snapshot after 90s, so this loaded the slow way (one request per 100 records).'
              break
            }
            /* Say what is happening. An unexplained wait is what sends somebody
             * to reload the page, which starts the whole thing again. */
            /* ⚠️ STATUS STAYS 'loading'. Every consumer of this hook branches on
             * `status === 'loading' | 'done' | 'error'`; adding a fourth value
             * would match none of them and render an EMPTY board while the
             * snapshot built — a silent zero dressed up as a fix. The progress
             * rides alongside instead. */
            setLoadedCount(snap.progress?.records || 0)
            setBuilding({ records: snap.progress?.records || 0, elapsedSeconds: snap.progress?.elapsedSeconds || 0 })
            await new Promise((r) => setTimeout(r, SNAPSHOT_POLL_MS))
            continue
          }

          if (snap?.ok && Array.isArray(snap.records) && snap.records.length) {
            /* ⚠️ THE FAN-OUT IS STAMPED ONLY ON A COMPLETE UNIVERSE.
             * `citationShared` counts how many crowned records cite one URL, so
             * a count taken over a partial list under-reports — and an
             * under-reported count reads as "this page backs one record", which
             * is the reassuring answer. Stamped here (whole snapshot) and after
             * the cursor walk finishes; the progressive setData below
             * deliberately leaves it unstamped, and `citationReuse()` is silent
             * without it rather than guessing. */
            const recs = attachCitationFanout(normalizeChampionships(snap.records))
            setData(recs)
            setLoadedCount(recs.length)
            setPageCount(1)
            setComplete(true)
            setLoadMode('snapshot')
            setReadEvidence(championshipSourceEvidence(snap))
            setReadScope(snap.scope || null)
            /* The age is surfaced, not hidden — a cached read is not a live one,
             * and a board that implies it is watching HubSpot live is how a
             * champion gets crowned twice.
             *
             * ⚠️ DERIVED FROM `asOf`, not sent as a field. `ageSeconds` changes
             * every second, so keeping it in the body would bust the server's
             * per-build gzip cache on every single request and put a multi-MB
             * re-compress back on the hot path. */
            const built = snap.asOf ? Date.parse(snap.asOf) : NaN
            const ageSeconds = Number.isFinite(built) ? Math.max(0, Math.round((Date.now() - built) / 1000)) : null
            setAsOf({ asOf: snap.asOf, ageSeconds, cached: ageSeconds != null && ageSeconds > 2, refreshError: snap.refreshError })
            setBuilding(null)
            setStatus('done')
            return
          }
          degradedReason = 'The championship snapshot returned no records, so this loaded the slow way.'
          break
        }
      } catch (e) {
        // An obsolete/aborted load must not repaint the current snapshot receipt.
        if (signal?.aborted || rid !== runId.current) return
        /* ⚠️ NOT SILENT ANY MORE. The fallback still runs — the board must load
         * — but it now SAYS it degraded and why. A silent fallback is how a
         * 148-request walk hid behind a fix that was already deployed. */
        degradedReason = `The fast path failed (${e?.message || e}), so this loaded the slow way — one request per 100 records.`
      }
      setBuilding(null)
      if (degradedReason) setDegraded(degradedReason)

      const all = []
      let pageEvidence = null
      let after
      let pages = 0
      const seen = new Set() // a repeated cursor is an incomplete load, not exhaustion
      try {
        do {
          const res = await api.championships({ signal, query: { limit: PAGE, after } })
          if (signal?.aborted || rid !== runId.current) return
          pageEvidence = championshipSourceEvidence(res, pageEvidence)
          setReadEvidence(pageEvidence)
          setReadScope(res.scope || null)
          const recs = normalizeChampionships(res)
          all.push(...recs)
          // Progressive render: update as pages arrive.
          setData(all.slice())
          setLoadedCount(all.length)
          // Backend returns the next cursor as `paging` (string) or {next:...}.
          const next =
            typeof res?.paging === 'string'
              ? res.paging
              : res?.paging?.next?.after || res?.paging?.after || null
          pages += 1
          // Preserve the unfinished cursor so a loop cannot claim completeness.
          after = next
          if (next && seen.has(next)) {
            setError(`Loading stopped after ${all.length} records because the API repeated a pagination cursor. Retry the full load.`)
            break
          }
          if (next) seen.add(next)
        } while (after && pages < RUNAWAY_PAGES && !signal?.aborted)
        if (signal?.aborted || rid !== runId.current) return
        // Evidence fan-out is meaningful only after genuine cursor exhaustion.
        setData(after ? all : attachCitationFanout(all))
        setPageCount(pages)
        setComplete(!after) // true = cursor exhausted, this IS the whole object
        setLoadMode('paged')
        setStatus('done')
      } catch (err) {
        if (err?.name === 'AbortError' || signal?.aborted || rid !== runId.current) return
        setPageCount(pages)
        setLoadMode('paged')
        // If we got at least some data, surface it but flag the partial error.
        if (all.length) { setStatus('done'); setError(`Loaded ${all.length} before an error: ${err?.message || err}`) }
        else { setError(err?.message || 'Could not load championships.'); setStatus('error') }
      }
    },
    [demo]
  )

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  const reload = useCallback(() => load(), [load])

  // Optimistic local patch after a write (crown / field-correct) so the UI
  // updates instantly without re-paging the full ~12k universe. The source of
  // truth is HubSpot; this only reflects a just-confirmed write locally.
  const patchLocal = useCallback((id, patch) => {
    setData((list) => list.map((c) => (c.id === id ? { ...c, ...patch } : c)))
  }, [])

  return { data, status, error, reload, loadedCount, pageCount, complete, loadMode, demo, patchLocal, asOf, degraded, building, readEvidence, readScope }
}
